import { usersApiClient } from './api';

export interface ScenarioOutcome {
  choiceText: string;
  resultText: string;
  isRecommended: boolean;
  points: number;
  _id: string;
}

export interface ScenarioDetails {
  story: string;
  outcomes: ScenarioOutcome[];
}

export interface ScenarioQuestion {
  _id: string;
  title: string;
  story: string;
  options: {
    text: string;
    isCorrect: boolean;
    _id: string;
  }[];
  explanation: string;
  bibleReference: {
    book: string;
    chapter: number;
    verses: string;
    text?: string;
  };
  categories: string[];
  pointMultiplier: number;
  isActive: boolean;
  createdAt: string;
  scenarioDetails?: ScenarioDetails;
}

export interface ScenarioGameResult {
  pointsEarned: number;
  correctAnswers: number;
  totalQuestions: number;
  scenarios: ScenarioChoice[];
}

export interface ScenarioChoice {
  scenarioId: string;
  choiceText: string;
  points: number;
  isRecommended: boolean;
}

export interface ScenarioGameResponse {
  message: string;
  pointsEarned: number;
  user: any;
}

export interface ScenarioStatus {
  gamesPlayedToday: number;
  gamesRemaining: number; // -1 means unlimited
  totalScenariosAvailable: number;
  resetTime: string | null; // null for unlimited games
}

class ScenarioService {
  private baseURL = '/scenarios';

  // Get random scenarios for gameplay
  async getRandomScenarios(limit: number = 3): Promise<{ scenarios: ScenarioQuestion[]; totalAvailable: number }> {
    try {
      const response = await usersApiClient.get(`${this.baseURL}/random?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching random scenarios:', error);
      throw error;
    }
  }

  // Get user's scenario game status (unlimited play)
  async getScenarioStatus(auth0Id: string): Promise<ScenarioStatus> {
    try {
      const response = await usersApiClient.get(`${this.baseURL}/status/${auth0Id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching scenario status:', error);
      throw error;
    }
  }

  // Submit scenario game results
  async submitScenarioResults(auth0Id: string, result: ScenarioGameResult): Promise<ScenarioGameResponse> {
    try {
      const response = await usersApiClient.post(
        `${this.baseURL}/submit-result?auth0Id=${auth0Id}`,
        result
      );
      return response.data;
    } catch (error) {
      console.error('Error submitting scenario results:', error);
      throw error;
    }
  }

  // Get all scenarios (for admin purposes)
  async getAllScenarios(): Promise<ScenarioQuestion[]> {
    try {
      const response = await usersApiClient.get(this.baseURL);
      return response.data;
    } catch (error) {
      console.error('Error fetching all scenarios:', error);
      throw error;
    }
  }
}

const scenarioService = new ScenarioService();
export default scenarioService;
